/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { Matiere } from "./matiere.type";
import { useLocationState } from "../../Utils";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const mergeUserAndDefaultMatieres = (
    userMatieres: Matiere[],
    defaultMatieres: Matiere[]
): Matiere[] => {
    const userMatiereIds = new Set(userMatieres.map((matiere) => matiere.id));

    return [
        ...userMatieres,
        ...defaultMatieres.filter((matiere) => !userMatiereIds.has(matiere.id)),
    ];
};

// Requête Firestore directe (attendue), à utiliser à la création d'un
// compte pour activer les matières par défaut (Coran, Tajwid, Arabe) sans
// dépendre du hook useGetDefaultMatieresQuery, dont le cache peut ne pas
// encore être résolu au moment de la soumission du formulaire d'inscription
// (le champ enabledMatieres partait alors vide silencieusement).
export const fetchDefaultMatiereIds = async (): Promise<string[]> => {
    try {
        const q = query(
            collection(db, "matieres"),
            where("isDefaultMatiere", "==", true),
            where("archived", "==", false),
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => doc.id);
    } catch (error) {
        console.error("Erreur récupération des matières par défaut:", error);
        return [];
    }
};


export const MatiereApi = createApi({
    reducerPath: "matiereApi",
    tagTypes: ["matiere", "matieres","defaultMatieres","matieresArchivees"],
    baseQuery: fakeBaseQuery(),
    endpoints: (builder) => ({
        addOrEditMatiere: builder.mutation({
            async queryFn({ id, ...data }) {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const userId = user?.uid;
                    if (!id) {
                        const docRef = await addDoc(collection(db, "matieres"), { 
                            ...data, 
                            createdAt: 
                            serverTimestamp(), 
                            createdBy: userId, 
                            updatedAt: serverTimestamp(),
                            archived: false,
                            isActive: true,
                        });
                        return {
                            data: {
                                id: docRef.id,
                                message: "Matière créée !",
                                ...data,
                            },
                        };

                    } else {
                        // Mettre à jour Firestore
                        const docRef = doc(db, "matieres", id);
                        await updateDoc(docRef, { ...data, updatedAt: serverTimestamp() });

                        return {
                            data: {
                                message:
                                    "Matière modifiée  !",
                            },
                        };
                    }
                } catch (error: any) {
                    return { error: { status: "CUSTOM", message: error.message } };
                }
            },
            invalidatesTags: ["matiere", "matieres", "defaultMatieres"],
        }),
        getMatieres: builder.query({
            async queryFn() {
                try {
                    return new Promise((resolve) => {
                        const q = query(
                            collection(db, "matieres"),
                            where('archived', '==', false),
                            orderBy("createdAt", "desc"),
                        );
                        onSnapshot(q, (snapshot) => {
                            const matieres = snapshot.docs.map((doc) => ({
                                id: doc.id,
                                ...doc.data(),
                            }));
                            resolve({ data: matieres as Matiere[] });
                        });
                        // unsubscribe();
                    });
                } catch (error) {
                    return { error };
                }
            },
            keepUnusedDataFor: 0,
            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                const matieresRef = query(
                    collection(db, "matieres"),
                    where('archived', '==', false),
                    orderBy("createdAt", "desc"),
                );
                let unsubscribe;
                try {
                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(matieresRef, (snapshot) => {
                        const matieres = snapshot.docs.map((doc) => ({
                            id: doc.id,
                            ...doc.data(),
                        }));
                        updateCachedData(() => matieres as Matiere[]);
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["matieres"],
        }),
        getDefaultMatieres: builder.query<Matiere[], void>({
            async queryFn() {
                try {
                const q = query(
                    collection(db, "matieres"),
                    where("isDefaultMatiere", "==", true),
                    where("archived", "==", false),
                    orderBy("createdAt", "asc")
                );
                const snapshot = await getDocs(q);
                const matieres = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                })) as Matiere[];
                return { data: matieres };
                } catch (error) {
                console.error("Erreur récupération matières par défaut:", error);
                return { error };
                }
            },
            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                let unsubscribe: (() => void) | undefined;
                try {
                await cacheDataLoaded;

                const matieresRef = query(
                    collection(db, "matieres"),
                    where("isDefaultMatiere", "==", true),
                    where("archived", "==", false),
                    orderBy("createdAt", "asc")
                );

                unsubscribe = onSnapshot(matieresRef, (snapshot) => {
                    const matieres = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                    })) as Matiere[];
                    updateCachedData(() => matieres);
                });
                } catch (error) {
                console.error("Erreur de souscription:", error);
                }

                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["defaultMatieres"],
        }),
        getMatiereByProf: builder.query({
            async queryFn() {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const userId = user?.uid;
                    return new Promise((resolve) => {
                        const q = query(
                            collection(db, "matieres"),
                            where("createdBy", "==", userId),
                            where('archived', '==', false),
                            orderBy("createdAt", "asc"),
                        );
                        onSnapshot(q, (snapshot) => {
                            const matieres = snapshot.docs.map((doc) => ({
                                id: doc.id,
                                ...doc.data(),
                            }));
                            resolve({ data: matieres as Matiere[] });
                        });
                        // unsubscribe();
                    });
                } catch (error) {
                    return { error };
                }
            },
            keepUnusedDataFor: 0,
            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                const user = auth.currentUser;

                const userId = user?.uid;
                const matieresRef = query(
                    collection(db, "matieres"),
                    where("createdBy", "==", userId),
                    where('archived', '==', false),
                    orderBy("createdAt", "asc"),
                );
                let unsubscribe;
                try {

                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(matieresRef, (snapshot) => {
                        const matieres = snapshot.docs.map((doc) => ({
                            id: doc.id,
                            ...doc.data(),
                        }));
                        updateCachedData(() => matieres as Matiere[]);
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["matieres"],
        }),
        // Quota partagé d'un établissement : matières créées directement par
        // l'établissement + matières créées par chacun de ses professeurs.
        getActiveMatieresByOrganization: builder.query({
            async queryFn(organizationId: string) {
                if (!organizationId) return { data: [] };
                try {
                    const teachersSnapshot = await getDocs(query(
                        collection(db, "users"),
                        where("userType", "==", "teacher"),
                        where("createdBy", "==", organizationId),
                    ));
                    const creatorIds = [organizationId, ...teachersSnapshot.docs.map((teacher) => teacher.id)];
                    const chunks: string[][] = [];
                    for (let index = 0; index < creatorIds.length; index += 30) {
                        chunks.push(creatorIds.slice(index, index + 30));
                    }
                    const snapshots = await Promise.all(chunks.map((ids) => getDocs(query(
                        collection(db, "matieres"),
                        where("createdBy", "in", ids),
                        where("archived", "==", false),
                    ))));
                    return {
                        data: snapshots.flatMap((snapshot) => snapshot.docs.map((matiereDoc) => ({
                            id: matiereDoc.id,
                            ...matiereDoc.data(),
                        }))) as Matiere[],
                    };
                } catch (error) {
                    return { error };
                }
            },
            providesTags: ["matieres"],
        }),
        getMatieresByTeacherId: builder.query({
            async queryFn(teacherId: string) {
                if (!teacherId) return { data: [] };
                try {
                    const matieresQuery = query(
                        collection(db, "matieres"),
                        where("createdBy", "==", teacherId),
                        where("archived", "==", false),
                        orderBy("createdAt", "asc"),
                    );
                    const snapshot = await getDocs(matieresQuery);
                    return {
                        data: snapshot.docs.map((matiereDoc) => ({
                            id: matiereDoc.id,
                            ...matiereDoc.data(),
                        })) as Matiere[],
                    };
                } catch (error) {
                    return { error };
                }
            },
            providesTags: ["matieres"],
        }),
        getMatiereByUser: builder.query({
            async queryFn() {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const userId = user?.uid;
                    const userMatieresQuery = query(
                        collection(db, "matieres"),
                        where("createdBy", "in", [userId, "admin"]),
                        where('archived', '==', false),
                        orderBy("createdAt", "asc"),
                    );
                    const defaultMatieresQuery = query(
                        collection(db, "matieres"),
                        where("isDefaultMatiere", "==", true),
                        where('archived', '==', false),
                        orderBy("createdAt", "asc"),
                    );

                    const [userSnapshot, defaultSnapshot] = await Promise.all([
                        getDocs(userMatieresQuery),
                        getDocs(defaultMatieresQuery),
                    ]);
                    const userMatieres = userSnapshot.docs.map((doc) => ({
                        id: doc.id,
                        ...doc.data(),
                    })) as Matiere[];
                    const defaultMatieres = defaultSnapshot.docs.map((doc) => ({
                        id: doc.id,
                        ...doc.data(),
                    })) as Matiere[];

                    return { data: mergeUserAndDefaultMatieres(userMatieres, defaultMatieres) };
                } catch (error) {
                    return { error };
                }
            },
            keepUnusedDataFor: 0,
            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                const user = auth.currentUser;

                const userId = user?.uid;
                if (!userId) return;

                const userMatieresRef = query(
                    collection(db, "matieres"),
                    where("createdBy", "in", [userId, "admin"]),
                    where('archived', '==', false),
                    orderBy("createdAt", "asc"),
                );
                const defaultMatieresRef = query(
                    collection(db, "matieres"),
                    where("isDefaultMatiere", "==", true),
                    where('archived', '==', false),
                    orderBy("createdAt", "asc"),
                );
                let unsubscribeUserMatieres: (() => void) | undefined;
                let unsubscribeDefaultMatieres: (() => void) | undefined;
                let userMatieres: Matiere[] = [];
                let defaultMatieres: Matiere[] = [];
                const updateMatieres = () => {
                    updateCachedData(() => mergeUserAndDefaultMatieres(userMatieres, defaultMatieres));
                };
                try {

                    await cacheDataLoaded;

                    unsubscribeUserMatieres = onSnapshot(userMatieresRef, (snapshot) => {
                        userMatieres = snapshot.docs.map((doc) => ({
                            id: doc.id,
                            ...doc.data(),
                        })) as Matiere[];
                        updateMatieres();
                    });
                    unsubscribeDefaultMatieres = onSnapshot(defaultMatieresRef, (snapshot) => {
                        defaultMatieres = snapshot.docs.map((doc) => ({
                            id: doc.id,
                            ...doc.data(),
                        })) as Matiere[];
                        updateMatieres();
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                unsubscribeUserMatieres?.();
                unsubscribeDefaultMatieres?.();
            },
            providesTags: ["matieres", "defaultMatieres"],
        }),
        getMatieresArchives: builder.query({
            async queryFn() {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const userId = user?.uid;
                    return new Promise((resolve) => {
                        const q = query(
                            collection(db, "matieres"),
                            where("createdBy", "in", [userId, "admin"]),
                            where('archived', '==', true),
                            orderBy("createdAt", "asc"),
                        );
                        onSnapshot(q, (snapshot) => {
                            const matieres = snapshot.docs.map((doc) => ({
                                id: doc.id,
                                ...doc.data(),
                            }));
                            resolve({ data: matieres as Matiere[] });
                        });
                        // unsubscribe();
                    });
                } catch (error) {
                    return { error };
                }
            },
            keepUnusedDataFor: 0,
            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                const user = auth.currentUser;

                const userId = user?.uid;
                const matieresRef = query(
                    collection(db, "matieres"),
                    where("createdBy", "in", [userId, "admin"]),
                    where('archived', '==', true),
                    orderBy("createdAt", "asc"),
                );
                let unsubscribe;
                try {

                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(matieresRef, (snapshot) => {
                        const matieres = snapshot.docs.map((doc) => ({
                            id: doc.id,
                            ...doc.data(),
                        }));
                        updateCachedData(() => matieres as Matiere[]);
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["matieresArchivees"],
        }),
        getMatiereById: builder.query({
            queryFn: async (matiereId) => {
                try {
                    const matiereRef = doc(db, "matieres", matiereId);
                    const matiereSnap = await getDoc(matiereRef);

                    if (!matiereSnap.exists()) {
                        return {
                            error: { status: 404, message: "Matière introuvable !" },
                        };
                    }

                    return { data: { id: matiereSnap.id, ...matiereSnap.data() } };
                } catch (error: any) {
                    return { error: { status: 500, message: error.message } };
                }
            },
            providesTags: ["matiere"],
        }),
        // Supprimer
        deleteMatiere: builder.mutation({
            queryFn: async (uid) => {
                try {
                    const docRef = doc(db, "matieres", uid);
                    await deleteDoc(docRef);
                    return {
                        data: { uid, message: "Matière supprimée !", success: true },
                    };
                } catch (error: any) {
                    return { error: { status: "DELETE_ERROR", message: error.message } };
                }
            },
            invalidatesTags: ["matiere", "matieres", "defaultMatieres"],
        }),
        archiveMatiere: builder.mutation({
            queryFn: async (matiereId) => {
                try {
                await updateDoc(doc(db, 'matieres', matiereId), {
                    archived: true,
                    archivedAt: serverTimestamp(),
                });
                return { 
                    data: { matiereId, message: "Matière archivée !", success: true },
                };
                } catch (error:any) {
                return { error: { status: "ARCHIVE_ERROR", message: error.message } };
                }
            },
            invalidatesTags: ["matiere", "matieres","defaultMatieres"],
        }),
        unArchiveMatiere: builder.mutation({
            queryFn: async (matiereId) => {
                try {
                await updateDoc(doc(db, 'matieres', matiereId), {
                    archived: false,
                    archivedAt: serverTimestamp(),
                });
                return { 
                    data: { matiereId, message: "Matière désarchivée !", success: true },
                };
                } catch (error:any) {
                return { error: { status: "ARCHIVE_ERROR", message: error.message } };
                }
            },
            invalidatesTags: ["matiere", "matieres","defaultMatieres"],
        }),
    }),
});

export const {
    useAddOrEditMatiereMutation,
    useGetMatieresQuery,
    useGetMatiereByUserQuery,
    useGetMatiereByProfQuery,
    useGetActiveMatieresByOrganizationQuery,
    useGetMatieresByTeacherIdQuery,
    useDeleteMatiereMutation,
    useLazyGetMatiereByIdQuery,
    useGetDefaultMatieresQuery,
    useArchiveMatiereMutation,
    useGetMatieresArchivesQuery,
    useUnArchiveMatiereMutation,
} = MatiereApi;

export function useMatiereFromLocation(): [
    Matiere,
    boolean,
    string,
    (id: string) => any
] {
    const itemState = useLocationState<Matiere | undefined>(undefined);
    const [item, setItem] = useState(itemState);
    const { id } = useParams<{ id: string }>();
    const [findById, { data, isError, isLoading }] = useLazyGetMatiereByIdQuery();
    const navigate = useNavigate();

    useEffect(() => {
        // console.log("is id", id)
        if (id && id !== undefined) {
            // console.log("id", id)
            findById(id as string);
        } else {
            setItem(undefined)
        }
    }, [id]);
    useEffect(() => {
        if (isError && !itemState) {
            navigate(-1);
        }
    }, [isError]);
    useEffect(() => {
        if (data) {
            setItem(data as Matiere);
        }
    }, [data]);

    return [item as Matiere & { bio: string }, isLoading, id as string, findById];
}
